import { call, put, all } from 'redux-saga/effects'
import { END } from 'redux-saga'
import { fetchApi } from '../helpers/fetchApi'

import {
  fetchProductStoreSuccess,
  fetchProductStoreFailure,
  fetchCnetContentCheckRequest
} from '../../actionCreators/productStore/productStoreActionCreators'

import { fetchSpecialOffersRequest } from '../../actionCreators/specialOffers/specialOffersActionCreators'
import { fetchProductCareRequest } from '../../actionCreators/productCare/productCareActioCreators'

import Logger from '../../helpers/Logger'
import {
  comparisonIconsString,
  comparisonAttributesString,
  comparisonGroupsString
} from '../../helpers/specificationTransformation'
import { buildProductImagePlaceholder } from '../../helpers/amplienceHelper'
import { isValidSku } from '../../helpers/skuValidator'
import { fetchCmsMtoRequest, fetchCmsDeliveryRequest } from '../../actionCreators/cms/cmsActionCreators'
import { decodeHtmlEntities } from '../../helpers/decodeHtmlEntities'
import { getBrandLogoUrl } from '../../helpers/getBrandLogoUrl'

const logger = Logger.getInstance()
const apiVersion = 'application/vnd.argos-product.v2+json'
const endpoint = '/product-api/pdp-service/partNumber'
const fulfilmentPassFeatureType = '33'

const generateOverallFit = ratingSummary => {
  if (!ratingSummary) return {}
  const ratingAttributes = ratingSummary.attributes || {}
  const fitRatings = ratingAttributes.fitDistribution || []
  if (!fitRatings.length) return {}

  const totalNoOfFitRatings = fitRatings.reduce((acc, val) => acc + val.Count, 0)
  const getPercentage = value => Math.round(100 * (value / totalNoOfFitRatings))
  const smallPercentage = getPercentage(fitRatings[0].Count + fitRatings[1].Count)
  const perfect = getPercentage(fitRatings[2].Count)
  const bigPercentage = getPercentage(fitRatings[3].Count + fitRatings[4].Count)
  const overallFit = [
    { name: 'small', percentage: smallPercentage },
    { name: 'expected', percentage: perfect },
    { name: 'big', percentage: bigPercentage },
    { name: 'noOverall' }
  ]

  const getFitWhichPassesThreshold = overallFit.find(({ percentage }) => percentage > 50)
  const correctFit = getFitWhichPassesThreshold || overallFit.find(({ name }) => name === 'expected')
  return { ...correctFit, totalNoOfFitRatings }
}

const buildProductData = response => {
  logger.debug('functionLogger', { arguments: response }, 'ProductStore updateProduct')

  const legalText = response.included.find(item => item.type === 'legalTexts')
  const media = response.included.find(item => item.type === 'media')
  const ar = response.included.find(item => item.type === 'ar')
  const data = {
    attributes: response.attributes,
    badges: response.included.filter(item => item.type === 'badges'),
    productName: decodeHtmlEntities(response.attributes.name),
    creditPlansV5: response.included.find(item => item.type === 'creditplan_v5'),
    legalText: legalText && legalText.attributes,
    media: (media && media.attributes) || {},
    ar: (ar && ar.attributes) || {},
    prices: response.included.find(item => item.type === 'prices'),
    priorityMeta: response.meta && response.meta.priority,
    productId: response.id,
    relationships: response.relationships,
    ratingSummary: response.included.find(item => item.type === 'reviewstatistics'),
    quantity: response.quantity,
    taxonomycategory: response.included.find(item => item.type === 'taxonomy' && item.id === 'category'),
    variants: response.included.find(item => item.type === 'variants'),
    promotions: response.included.find(item => item.type === 'promotions_evaluate'),
    dimensions: response.included.find(item => item.type === 'dimensions'),
    bundles: response.included.filter(item => item.type === 'bundles'),
    energy: response.included.filter(item => item.type === 'energy'),
    brandLogoUrl: getBrandLogoUrl(response.attributes?.brand, response.brandLogos)
  }
  data.attributes.name = decodeHtmlEntities(response.attributes.name)
  const productCareData = response.included.find(item => item.type === 'productCare')
  data.productCare = (productCareData && productCareData.attributes) || {}
  const nutrition = response.included.find(item => item.type === 'nutrition')
  data.nutrition = (nutrition && nutrition.attributes) || {}
  const byIncludeTypes = typeList => item => typeList.includes(item.type)
  const getComparisonData = byIncludeTypes([comparisonIconsString, comparisonAttributesString, comparisonGroupsString])
  data.comparison = response.included.filter(getComparisonData)

  const warrantyDetails = response.included.find(item => item.type === 'warranties')
  data.warrantyProductDetails = warrantyDetails
    ? {
        id: warrantyDetails.id,
        attributes: {
          synapticaId: warrantyDetails.attributes.synapticaId
        }
      }
    : {}

  const emptyBreadcrumb = { attributes: { categories: [] } }
  const isTaxonomyBreadcrumb = ({ type, id }) => type === 'taxonomy' && id === 'breadcrumb'
  const { attributes } = response.included.find(isTaxonomyBreadcrumb) || emptyBreadcrumb
  const { categories = [] } = attributes
  // Filter out the Master Category
  const MASTER_CATEGORY_ID = '10000001'
  data.breadcrumb = categories.filter(category => category.id !== MASTER_CATEGORY_ID)
  data.templateType = attributes ? attributes.templateType : null

  const isBreadcrumb = ({ type }) => type === 'breadcrumb'
  const breadcrumb = response.included.find(isBreadcrumb)

  if (breadcrumb) {
    data.categories = breadcrumb.attributes.categories
  }

  // Set no image placeholder
  if (!data.media.images || !data.media.images.length) {
    data.media.images = [buildProductImagePlaceholder()]
  }

  if (data?.ratingSummary?.attributes) {
    data.ratingSummary.attributes.overallFit = generateOverallFit(data.ratingSummary)
  }

  return { data }
}

const product = state => state.productStore

function* productStoreSaga(action, baseUrl, cookies) {
  const { partNumber, quantity = 1, isServerSideCall = false } = action.payload
  try {
    if (!isValidSku(partNumber)) {
      const error = new Error('Invalid part number')
      error.status = 400
      error.productId = partNumber
      throw error
    }
    const url = `${baseUrl || ''}${endpoint}/${partNumber}`
    const brandLogosUrl = 'https://cdn.tu.co.uk/apps/nav-pdp/config/tu-brand-logos/logos.json'
    let headers = { Accept: apiVersion }
    if (cookies) {
      headers = {
        Cookie: cookies,
        ...headers
      }
    }
    const brandLogos = yield call(fetchApi, brandLogosUrl)
    const jsonData = yield call(fetchApi, url, null, headers)
    const response = {
      ...jsonData.data,
      included: jsonData.included,
      brandLogos,
      meta: jsonData.meta,
      quantity
    }

    // Temporary fix for products that should not be available on Habitat (due to product gateway bug)
    const BRAND = process.env.BRAND || 'ARGOS'

    const isHabitatPDP = BRAND === 'HABITAT'
    const channels = response?.attributes?.channels
    const hasNoHabitatChannel = channels && !channels.includes('HABITAT_UK')

    if (isHabitatPDP && hasNoHabitatChannel) {
      const error = new Error('Habitat Product missing HABITAT_UK channel')
      error.status = 404
      error.productId = partNumber
      throw error
    }

    const featureType = response?.attributes?.featureType

    if (featureType === fulfilmentPassFeatureType) {
      const error = new Error('Products with featureType 33 are fulfilment pass SKUs and are restricted.')
      error.status = 404
      error.productId = partNumber
      throw error
    }

    if (response?.attributes?.madeToOrder && isServerSideCall) {
      yield put(fetchCmsMtoRequest())
    }

    if (
      !response?.attributes?.madeToOrder &&
      isServerSideCall &&
      response?.attributes?.fulfilmentAgent === 'ADDO' &&
      !response?.attributes?.sdf
    ) {
      yield put(fetchCmsDeliveryRequest())
    }

    const successAction = fetchProductStoreSuccess(buildProductData(response))
    yield put(successAction)

    if (!partNumber.startsWith('tuc')) {
      yield put(fetchSpecialOffersRequest({ partNumber }, baseUrl))
      yield put(fetchCnetContentCheckRequest({ partNumber }))
    }

    const findProductCare = response.included.find(item => item.type === 'productCare')
    if (findProductCare && findProductCare.attributes && findProductCare.attributes.warranties) {
      yield all(
        findProductCare.attributes.warranties.map(care =>
          put(fetchProductCareRequest({ productCareSku: care.sku }, baseUrl))
        )
      )
    }

    if (baseUrl) {
      // If server side we stop all the watchers
      yield put(END)
    }
  } catch (error) {
    const response = {
      errorDetail: error.toString(),
      errorCode: error.status || 500,
      data: {
        productId: partNumber
      }
    }
    if (error.response && error.response.body && error.response.body.errors) {
      response.errorDetail = error.response.body.errors
    }
    if (error.status === 404) {
      logger.error(
        'apiLogger',
        { arguments: [error.status, error.message, error.productId], error },
        'productStoreSaga 404 Error'
      )
      response.data.productName = 'Product not found'
    } else {
      logger.error(
        'apiLogger',
        { arguments: [error.status, error.message, error.productId], error },
        `productStoreSaga ${error.status || 500} Error`
      )
    }

    const failureAction = fetchProductStoreFailure(response)
    yield put(failureAction)
    if (baseUrl) {
      // If server side we stop all the watchers
      yield put(END)
    }
  }
}

export { productStoreSaga, endpoint, product, buildProductData }
